import React, { useEffect } from "react";
import { Box, Button, Table, TextField, Typography } from "@material-ui/core";
import TableHead from "@material-ui/core/TableHead";
import TableCell from "@material-ui/core/TableCell";
import TableRow from "@material-ui/core/TableRow";
import { useDispatch, useSelector } from "react-redux";
import TableBody from "@material-ui/core/TableBody";
import StaffRole from "types/enum/StaffRole";
import EnumUtils from "utils/EnumUtils";
import { fetchCastName } from "redux/actions/castName";
import { fetchGuestNotesByCastNameId } from "redux/actions/guestNote";
import { DateTime } from "luxon";
import { FORMAT_TYPE } from "utils/DateTimeUtils";
import { Autocomplete } from "@material-ui/lab";
import CastNameRes from "types/res/castName/CastNameRes";
import GuestRank from "types/enum/GuestRank";
import { useTranslation } from "react-i18next";

const GuestNote = () => {
  const { t } = useTranslation("guest");
  const dispatch = useDispatch();
  const companyId = useSelector((state) => state.account.staff.companyId);
  const staffRole = useSelector((state) => state.account.staff.role);
  const castNames = useSelector((state) => state.castName);
  const guestNotes = useSelector((state) => state.guestNote);
  const [selectCastName, setSelectCastName] =
    React.useState<CastNameRes | null>(null);
  const [selectGuestNoteId, setSelectGuestNoteId] = React.useState<number>();
  useEffect(() => {
    dispatch(fetchCastName(companyId));
  }, [companyId]);

  useEffect(() => {
    if (!selectCastName?.castNameId) return;
    dispatch(fetchGuestNotesByCastNameId(companyId, selectCastName.castNameId));
  }, [selectCastName]);
  if (
    ![
      StaffRole.notelMaster,
      StaffRole.notelCallCenterEmployee,
      StaffRole.clientMaster,
    ].includes(
      EnumUtils.mapToEnum(StaffRole, staffRole) || StaffRole.clientShopPartTime
    )
  ) {
    return <Box>{t("note.adminRequired")}</Box>;
  }

  const guestNoteTable = () => {
    return (
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>{t("note.guestName")}</TableCell>
            <TableCell>{t("note.guestTel")}</TableCell>
            <TableCell>{t("note.lastUsedAt")}</TableCell>
            <TableCell />
          </TableRow>
        </TableHead>
        <TableBody>
          {guestNotes.map((guestNote) => (
            <TableRow key={guestNote.guestNoteId}>
              <TableCell>{guestNote.guest?.name}</TableCell>
              <TableCell>{guestNote.guest?.tel}</TableCell>
              <TableCell>
                {guestNote?.orders?.[0]?.orderDate
                  ? DateTime.fromJSDate(
                      guestNote?.orders?.[0]?.orderDate
                    ).toFormat(FORMAT_TYPE.YEAR_DAY)
                  : ""}
              </TableCell>
              <TableCell>
                <Box>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => setSelectGuestNoteId(guestNote.guestNoteId)}
                  >
                    {t("note.select")}
                  </Button>
                </Box>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    );
  };

  const guestNoteOne = () => {
    const guestNote = guestNotes.find(
      (note) => note.guestNoteId === selectGuestNoteId
    );
    return (
      <Box>
        <Box display={"flex"} margin={2}>
          <Button
            variant="contained"
            color="primary"
            onClick={() => setSelectGuestNoteId(undefined)}
          >
            {t("note.back")}
          </Button>
        </Box>
        <Box display={"flex"}>
          <Box
            display={"flex"}
            flexDirection={"column"}
            margin={2}
            width={"50%"}
          >
            <Typography
              variant="subtitle1"
              style={{ fontWeight: "bold", marginBottom: "10px" }}
            >
              {t("note.basicInfo")}
            </Typography>
            <Typography>{t("note.guestName")}:{guestNote?.guest?.name}</Typography>
            <Typography>{t("note.guestTel")}:{guestNote?.guest?.tel}</Typography>
            <Typography>{t("note.nickName")}:{guestNote?.nickName}</Typography>
            <Typography>
              {t("note.guestRank")}:{EnumUtils.mapToEnum(GuestRank, guestNote?.guestRank)}
            </Typography>
            <Typography>{t("note.work")}:{guestNote?.work}</Typography>
            <Typography>{t("note.personality")}:{guestNote?.personality}</Typography>
            <Typography>{t("note.bodyType")}:{guestNote?.bodyType}</Typography>
            <Typography>{t("note.pet")}:{guestNote?.pet}</Typography>
            <Typography>{t("note.cigarette")}:{guestNote?.cigarette}</Typography>
            <Typography>{t("note.home")}:{guestNote?.home}</Typography>
            <Typography>{t("note.address")}:{guestNote?.address}</Typography>
            <Typography>{t("note.favoriteType")}:{guestNote?.favoriteType}</Typography>
            <Typography>{t("note.favoriteFood")}:{guestNote?.favoriteFood}</Typography>
            <Typography>{t("note.favoriteColor")}:{guestNote?.favoriteColor}</Typography>
            <Typography>{t("note.allergies")}:{guestNote?.allergies}</Typography>
            <Typography>{t("note.usingFrequency")}:{guestNote?.usingFrequency}</Typography>
            <Typography>{t("note.remarks")}:{guestNote?.remarks}</Typography>
          </Box>
          <Box display={"flex"} flexDirection={"column"} margin={2}>
            <Typography
              variant="subtitle1"
              style={{ fontWeight: "bold", marginBottom: "10px" }}
            >
              {t("note.serviceInfo")}
            </Typography>
            <Typography>{t("note.favoritePlay")}:{guestNote?.favoritePlay}</Typography>
            <Typography>{t("note.genitalSize")}:{guestNote?.genitalSize}</Typography>
            <Typography>{t("note.genitalState")}:{guestNote?.genitalState}</Typography>
            <Typography>{t("note.playNum")}:{guestNote?.playNum}</Typography>
            <Typography>{t("note.erogenousZone")}:{guestNote?.erogenousZone}</Typography>
            <Typography>{t("note.request")}:{guestNote?.request}</Typography>
          </Box>
        </Box>
      </Box>
    );
  };

  return (
    <Box margin={2}>
      <Box display={"flex"} alignItems={"center"}>
        <Typography>{t("note.castName")}</Typography>
        <Autocomplete
          id="search-castName"
          size="small"
          options={castNames}
          getOptionLabel={(option) =>
            option.name ? `${option.name}(${option?.shop?.name})` : ""
          }
          style={{ width: 300, margin: 5 }}
          renderInput={(params) => <TextField {...params} autoFocus={true} />}
          value={selectCastName || null}
          onChange={(event: any, newValue: CastNameRes | null) => {
            setSelectCastName(newValue);
          }}
          renderOption={(option) => (
            <div>
              {option.name ? `${option.name}(${option?.shop?.name})` : ""}
            </div>
          )}
        />
      </Box>
      <Box marginTop={2}>
        {selectCastName?.castNameId ? (
          selectGuestNoteId ? (
            guestNoteOne()
          ) : (
            guestNoteTable()
          )
        ) : (
          <Box display={"flex"} justifyContent={"center"} marginTop={2}>
            {t("note.selectCastFirst")}
          </Box>
        )}
      </Box>
    </Box>
  );
};
export default GuestNote;
